/**
 * Returns Comparison Table Component
 *
 * Displays 1Y/3Y/5Y returns with category comparison:
 * - Fund returns vs category average for each time period
 * - Inline deviation bars showing under/over performance
 * - Visual indicators for positive/negative deviations
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatPercent } from "@/lib/utils";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

// ============================================================================
// Types
// ============================================================================

/**
 * Time period for returns comparison
 */
export type ReturnPeriod = "1Y" | "3Y" | "5Y";

/**
 * Return data for a single time period
 */
export interface PeriodReturn {
  /** Time period label */
  period: ReturnPeriod;
  /** Fund's return for this period (percentage) */
  fundReturn: number;
  /** Category average return for this period (percentage) */
  categoryReturn: number;
  /** Whether data is available for this period */
  isAvailable?: boolean;
  /** Whether category return is estimated (pending API support) */
  isCategoryEstimated?: boolean;
}

/**
 * Props for the ReturnsComparisonTable component
 */
export interface ReturnsComparisonTableProps {
  /** Fund name for display */
  fundName?: string;
  /** Category name for display */
  categoryName?: string;
  /** Returns data for each period */
  returns: PeriodReturn[];
  /** Optional CSS class name */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Maximum deviation percentage for bar scaling
 * Deviations beyond this are clamped for visual consistency
 */
const MAX_DEVIATION = 10;

/**
 * Period display labels
 */
const PERIOD_LABELS: Record<ReturnPeriod, string> = {
  "1Y": "1 Year",
  "3Y": "3 Years",
  "5Y": "5 Years",
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate deviation between fund and category returns
 */
function calculateDeviation(fundReturn: number, categoryReturn: number): number {
  return fundReturn - categoryReturn;
}

/**
 * Get bar width percentage (clamped to max deviation)
 */
function getBarWidth(deviation: number): number {
  const absDeviation = Math.abs(deviation);
  const clampedDeviation = Math.min(absDeviation, MAX_DEVIATION);
  return (clampedDeviation / MAX_DEVIATION) * 100;
}

/**
 * Get color classes based on deviation
 */
function getDeviationColors(deviation: number): {
  barColor: string;
  textColor: string;
  bgColor: string;
} {
  if (deviation > 0.5) {
    return {
      barColor: "bg-emerald-500",
      textColor: "text-emerald-700 dark:text-emerald-400",
      bgColor: "bg-emerald-50 dark:bg-emerald-950/30",
    };
  } else if (deviation < -0.5) {
    return {
      barColor: "bg-rose-500",
      textColor: "text-rose-700 dark:text-rose-400",
      bgColor: "bg-rose-50 dark:bg-rose-950/30",
    };
  }
  return {
    barColor: "bg-slate-400",
    textColor: "text-slate-600 dark:text-slate-400",
    bgColor: "bg-slate-50 dark:bg-slate-900/30",
  };
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Inline deviation bar visualization
 * Shows a centered bar that extends left (negative) or right (positive)
 */
function DeviationBar({ deviation }: { deviation: number }) {
  const width = getBarWidth(deviation);
  const colors = getDeviationColors(deviation);
  const isPositive = deviation >= 0;

  return (
    <div className="relative h-5 w-full">
      {/* Background track */}
      <div className="absolute inset-0 flex items-center">
        <div className="h-1.5 w-full rounded-full bg-muted" />
      </div>

      {/* Center line indicator */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border -translate-x-1/2" />

      {/* Deviation bar */}
      <div
        className={cn(
          "absolute top-1/2 h-2.5 rounded-full transition-all duration-300 -translate-y-1/2",
          colors.barColor
        )}
        style={{
          width: `${width / 2}%`,
          left: isPositive ? "50%" : `${50 - width / 2}%`,
        }}
      />

      {/* End dot for emphasis */}
      <div
        className={cn(
          "absolute top-1/2 h-3 w-3 rounded-full -translate-y-1/2 transition-all duration-300",
          colors.barColor,
          "ring-2 ring-background"
        )}
        style={{
          left: isPositive ? `calc(50% + ${width / 2}% - 6px)` : `calc(50% - ${width / 2}% - 6px)`,
        }}
      />
    </div>
  );
}

/**
 * Deviation indicator with icon and value
 */
function DeviationIndicator({ deviation }: { deviation: number }) {
  const colors = getDeviationColors(deviation);
  const isPositive = deviation > 0.5;
  const isNegative = deviation < -0.5;

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium",
        colors.bgColor,
        colors.textColor
      )}
    >
      {isPositive && <TrendingUp className="h-3 w-3" />}
      {isNegative && <TrendingDown className="h-3 w-3" />}
      {!isPositive && !isNegative && <Minus className="h-3 w-3" />}
      <span>
        {deviation > 0 ? "+" : ""}
        {deviation.toFixed(2)}%
      </span>
    </div>
  );
}

/**
 * Single row in the comparison table
 */
function ComparisonRow({
  periodReturn,
  isLast,
}: {
  periodReturn: PeriodReturn;
  isLast: boolean;
}) {
  const { period, fundReturn, categoryReturn, isAvailable = true, isCategoryEstimated = false } = periodReturn;
  const deviation = calculateDeviation(fundReturn, categoryReturn);

  if (!isAvailable) {
    return (
      <div
        className={cn(
          "grid grid-cols-[80px_1fr_80px_1fr_100px] gap-4 items-center py-3",
          !isLast && "border-b border-border/50"
        )}
      >
        <span className="text-sm font-medium">{PERIOD_LABELS[period]}</span>
        <span className="text-sm text-muted-foreground">--</span>
        <span className="text-sm text-muted-foreground">--</span>
        <div className="h-5" />
        <span className="text-xs text-muted-foreground">N/A</span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid grid-cols-[80px_1fr_80px_1fr_100px] gap-4 items-center py-3",
        !isLast && "border-b border-border/50"
      )}
    >
      {/* Period */}
      <span className="text-sm font-medium">{PERIOD_LABELS[period]}</span>

      {/* Fund Return */}
      <span
        className={cn(
          "text-sm font-semibold tabular-nums",
          fundReturn >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"
        )}
      >
        {formatPercent(fundReturn, 2)}
      </span>

      {/* Category Return */}
      <span className="text-sm text-muted-foreground tabular-nums">
        {formatPercent(categoryReturn, 2)}
        {isCategoryEstimated && (
          <span className="ml-1 text-xs text-muted-foreground/70" title="Estimated - pending API support">
            Est.
          </span>
        )}
      </span>

      {/* Deviation Bar */}
      <DeviationBar deviation={deviation} />

      {/* Deviation Value */}
      <DeviationIndicator deviation={deviation} />
    </div>
  );
}

/**
 * Table header row
 */
function TableHeader() {
  return (
    <div className="grid grid-cols-[80px_1fr_80px_1fr_100px] gap-4 items-center pb-2 border-b border-border">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Period
      </span>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Fund
      </span>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Category
      </span>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider text-center">
        Deviation
      </span>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        vs Avg
      </span>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Returns Comparison Table
 *
 * Displays a comparison of fund returns vs category average across multiple
 * time periods (1Y, 3Y, 5Y) with inline deviation bars for visual comparison.
 *
 * @example
 * ```tsx
 * <ReturnsComparisonTable
 *   fundName="Parag Parikh Flexi Cap Fund"
 *   categoryName="Flexi Cap"
 *   returns={[
 *     { period: "1Y", fundReturn: 22.5, categoryReturn: 18.2 },
 *     { period: "3Y", fundReturn: 15.8, categoryReturn: 12.4 },
 *     { period: "5Y", fundReturn: 18.2, categoryReturn: 14.6 },
 *   ]}
 * />
 * ```
 */
export function ReturnsComparisonTable({
  fundName,
  categoryName,
  returns,
  className,
}: ReturnsComparisonTableProps) {
  // Ensure we have returns for all standard periods
  const allPeriods: ReturnPeriod[] = ["1Y", "3Y", "5Y"];
  const returnsMap = new Map(returns.map((r) => [r.period, r]));

  const normalizedReturns = allPeriods.map((period) => {
    const existing = returnsMap.get(period);
    if (existing) return existing;
    return {
      period,
      fundReturn: 0,
      categoryReturn: 0,
      isAvailable: false,
    };
  });

  // Calculate overall performance summary
  const availableReturns = normalizedReturns.filter((r) => r.isAvailable !== false);
  const avgDeviation =
    availableReturns.length > 0
      ? availableReturns.reduce(
          (sum, r) => sum + calculateDeviation(r.fundReturn, r.categoryReturn),
          0
        ) / availableReturns.length
      : 0;
  const overallPerformance =
    avgDeviation > 0.5
      ? "outperforming"
      : avgDeviation < -0.5
        ? "underperforming"
        : "at-par";

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-lg font-semibold">Returns Comparison</CardTitle>
            {fundName && categoryName && (
              <p className="text-sm text-muted-foreground mt-1">
                {fundName} vs {categoryName} Average
              </p>
            )}
          </div>
          {availableReturns.length > 0 && (
            <div
              className={cn(
                "text-xs font-medium px-2 py-1 rounded-full",
                overallPerformance === "outperforming"
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                  : overallPerformance === "underperforming"
                    ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
                    : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
              )}
            >
              {overallPerformance === "outperforming"
                ? "Outperforming"
                : overallPerformance === "underperforming"
                  ? "Underperforming"
                  : "At Par"}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-0">
          <TableHeader />
          {normalizedReturns.map((periodReturn, index) => (
            <ComparisonRow
              key={periodReturn.period}
              periodReturn={periodReturn}
              isLast={index === normalizedReturns.length - 1}
            />
          ))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-border/50">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="h-2 w-2 rounded-full bg-emerald-500" />
            <span>Outperforming category</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="h-2 w-2 rounded-full bg-rose-500" />
            <span>Underperforming category</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="h-2 w-2 rounded-full bg-slate-400" />
            <span>At par with category</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for ReturnsComparisonTable
 */
export function ReturnsComparisonTableSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-56" />
          </div>
          <Skeleton className="h-6 w-24 rounded-full" />
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-0">
          {/* Header */}
          <div className="grid grid-cols-[80px_1fr_80px_1fr_100px] gap-4 items-center pb-2 border-b border-border">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-10" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-16 mx-auto" />
            <Skeleton className="h-3 w-12" />
          </div>

          {/* Rows */}
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className={cn(
                "grid grid-cols-[80px_1fr_80px_1fr_100px] gap-4 items-center py-3",
                i < 2 && "border-b border-border/50"
              )}
            >
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-2.5 w-full rounded-full" />
              <Skeleton className="h-5 w-16 rounded-md" />
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-border/50">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <Skeleton className="h-2 w-2 rounded-full" />
              <Skeleton className="h-3 w-24" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default ReturnsComparisonTable;
